import { useReactiveVar } from '@bestseller/retail-planning.utils.reactive-vars';
import { useEffect, useMemo } from 'react';
import { Module } from 'src/domain';
import { selectedInheritanceMapVar } from 'src/infrastructure/local_state';
import { SerializeMap } from 'src/utils/SerializeMap';
import { useCurrentId } from '../useCurrentId';
import { useCurrentModule } from '../useCurrentModule';
import { useScope } from '../useScope';
import { useUnsavedInheritance } from './useStoredInheritance';

export const useSelectedInheritance = (selectedModule?: Module) => {
    const [selectedInheritanceMap, setSelectedInheritanceMap] = useReactiveVar(selectedInheritanceMapVar);
    const scope = useScope();
    const id = useCurrentId();
    const currentModule = useCurrentModule();
    const module = selectedModule ?? currentModule;

    const { inheritance: sessionStorageInheritance } = useUnsavedInheritance(module);

    useEffect(() => {
        if (!scope || !id || !module) {
            return;
        }

        const selectedInheritance = selectedInheritanceMap.get({ module, scope, id });

        // Fixes stale inheritance UI after unsaved-change expiration:
        // IndexedDB can be cleared while this reactive map still contains old values.
        // Removing that stale entry forces consumers to rerender with current inheritance.
        if (sessionStorageInheritance === null && selectedInheritance !== undefined) {
            setSelectedInheritanceMap((prev) => {
                const newMap = new SerializeMap(prev);
                newMap.delete({ module, scope, id });
                return newMap;
            });
        }
    }, [id, module, scope, selectedInheritanceMap, sessionStorageInheritance, setSelectedInheritanceMap]);

    return useMemo(() => {
        if (!scope || !id || !module) return;
        return selectedInheritanceMap.get({ module: module, scope, id }) ?? sessionStorageInheritance;
    }, [id, selectedInheritanceMap, module, scope, sessionStorageInheritance]);
};
